import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Slider, CircularProgress } from '@mui/material';
import url from '../../../../serverUrl';
import { api as axios } from '../../../../api';
import {
  connectWallet,
  describeError,
  describeResult,
  displaySymbolOf,
  fmtPrice,
  fmtQty,
  fmtUsd,
  num,
  priceDecimals,
  quoteFor,
  tokenLogo
} from './format';
import { useToast } from './Toast';
import './pro.css';

// ---------------------------------------------------------------------------
// Order entry panel (Market / Limit, Buy / Sell).
//
// Sends exactly the requests the old swap panel sent:
//   market buy   POST /crypto/marketOrder      { symbol, askPrice, quantity }
//   market sell  POST /crypto/sellmarketOrder  { symbol, askPrice, quantity }
//   limit buy    POST /crypto/placeBid         { symbol, askPrice, quantity, swapType, scheduleTime, partialOrder }
//   limit sell   POST /crypto/placeAsk         { symbol, bidPrice, quantity, swapType, scheduleTime }
// with `ask` / `bid` coming from the same book feed as before, so pricing is
// unchanged. What's new is the presentation: available balance, a size
// slider, an order summary, a confirm dialog and toasts instead of alerts.
// ---------------------------------------------------------------------------

const PCT_MARKS = [0, 25, 50, 75, 100].map((v) => ({ value: v }));

const balanceOf = (walletAssets, symbol) => {
  if (!walletAssets || !symbol) return null;
  const row = (walletAssets.tokens || []).find((t) => t.symbol === symbol);
  if (row) return num(row.amount) ?? 0;
  if (symbol === 'PECU' && walletAssets.pecu !== undefined) return num(walletAssets.pecu) ?? 0;
  return 0;
};

export default function OrderForm({
  token,
  PecuPrice,
  USXMPrice,
  bid = [],
  ask = [],
  auth_token,
  walletAssets,
  fetchWalletAsset,
  pickedPrice,
  initialSide = 'buy',
  onSubmitted
}) {
  const toast = useToast();
  const [side, setSide] = useState(initialSide);
  const [kind, setKind] = useState('market');
  const [amount, setAmount] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lock = useRef(false);

  const base = displaySymbolOf(token);
  const quote = quoteFor(token, PecuPrice, USXMPrice);
  const refPrice = num(token?.currentPrice) ?? num(token?.token_price);
  // Same references the old panel used: buy market prices off ask[0],
  // sell market shows bid[0]; both fall back to the token's price.
  const askPrice = num(ask?.[0]?.price) || refPrice || 0;
  const bidPrice = num(bid?.[0]?.price) || refPrice || 0;
  const connected = Boolean(auth_token);

  useEffect(() => setSide(initialSide), [initialSide]);

  // New market: clear the ticket.
  useEffect(() => {
    setAmount('');
    setLimitPrice('');
  }, [base]);

  // Clicking a level in the order book loads it as a limit price.
  useEffect(() => {
    if (pickedPrice && num(pickedPrice.price)) {
      setKind('limit');
      setLimitPrice(String(pickedPrice.price));
    }
  }, [pickedPrice]);

  useEffect(() => {
    if (kind === 'limit' && !limitPrice && refPrice) {
      setLimitPrice(refPrice.toFixed(priceDecimals(refPrice)));
    }
  }, [kind, limitPrice, refPrice]);

  const execPrice =
    kind === 'limit' ? num(limitPrice) || 0 : side === 'buy' ? askPrice : bidPrice;
  const qty = num(amount) || 0;
  const usdValue = qty * execPrice;
  const quoteValue = quote.price ? usdValue / quote.price : null;

  const quoteBal = balanceOf(walletAssets, quote.symbol);
  const baseBal = balanceOf(walletAssets, base);
  const maxQty = useMemo(() => {
    if (side === 'sell') return baseBal;
    if (quoteBal === null || !quote.price || !execPrice) return null;
    return (quoteBal * quote.price) / execPrice;
  }, [side, baseBal, quoteBal, quote.price, execPrice]);
  const pct = maxQty ? Math.min(100, (qty / maxQty) * 100) : 0;

  const insufficient =
    connected && walletAssets && qty > 0 && maxQty !== null && qty > maxQty * 1.000001;
  const invalid = !qty || qty <= 0 || !execPrice || execPrice <= 0;

  const setPct = (p) => {
    if (!maxQty) return;
    const q = (maxQty * p) / 100;
    setAmount(q > 0 ? String(Number(q.toFixed(6))) : '');
  };

  const swapType =
    kind === 'market'
      ? side === 'buy' ? 'BUY MARKET ORDER' : 'SELL MARKET ORDER'
      : side === 'buy' ? 'BUY LIMIT ORDER' : 'SELL LIMIT ORDER';

  const submit = async () => {
    if (lock.current) return;
    if (!localStorage.getItem('hootdex_secretcookie_wallet') || !auth_token) {
      toast.error('Connect your wallet to trade');
      return;
    }
    lock.current = true;
    setSubmitting(true);
    const headers = { Authorization: `Bearer ${auth_token}` };
    const symbol = token?.symbol || token?.token_symbol;
    let req;
    if (swapType === 'BUY MARKET ORDER') {
      req = axios.post(`${url}/crypto/marketOrder`, { symbol, askPrice, quantity: amount }, { headers });
    } else if (swapType === 'SELL MARKET ORDER') {
      req = axios.post(`${url}/crypto/sellmarketOrder`, { symbol, askPrice, quantity: amount }, { headers });
    } else if (swapType === 'SELL LIMIT ORDER') {
      req = axios.post(
        `${url}/crypto/placeAsk`,
        { symbol, bidPrice: limitPrice, quantity: amount, swapType, scheduleTime: '' },
        { headers }
      );
    } else {
      req = axios.post(
        `${url}/crypto/placeBid`,
        { symbol, askPrice: limitPrice, quantity: amount, swapType, scheduleTime: '', partialOrder: false },
        { headers }
      );
    }
    try {
      const res = await req;
      const r = describeResult(res.data);
      if (r.ok) {
        toast.success(`${side === 'buy' ? 'Buy' : 'Sell'} ${fmtQty(qty)} ${base}: ${r.text}`);
        setAmount('');
      } else {
        toast.error(r.text);
      }
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      lock.current = false;
      setSubmitting(false);
      setConfirmOpen(false);
      fetchWalletAsset && fetchWalletAsset();
      window.dispatchEvent(new Event('hd:orders-changed'));
      onSubmitted && onSubmitted();
    }
  };

  const sideCls = side === 'buy' ? 'x-buy' : 'x-sell';

  return (
    <div className="x-of">
      <div className="x-of-kinds" role="tablist">
        {['market', 'limit'].map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            className={kind === k ? 'x-of-kind x-active' : 'x-of-kind'}
            onClick={() => setKind(k)}
          >
            {k === 'market' ? 'Market' : 'Limit'}
          </button>
        ))}
      </div>

      <div className="x-of-sides">
        <button
          type="button"
          className={side === 'buy' ? 'x-of-side x-buy x-active' : 'x-of-side'}
          onClick={() => setSide('buy')}
        >
          Buy
        </button>
        <button
          type="button"
          className={side === 'sell' ? 'x-of-side x-sell x-active' : 'x-of-side'}
          onClick={() => setSide('sell')}
        >
          Sell
        </button>
      </div>

      <div className="x-of-row">
        <span className="x-muted">Available</span>
        <span>
          {!connected
            ? '—'
            : side === 'buy'
            ? `${fmtQty(quoteBal ?? 0)} ${quote.symbol}`
            : `${fmtQty(baseBal ?? 0)} ${base}`}
        </span>
      </div>

      {kind === 'limit' ? (
        <label className="x-field">
          <span className="x-field-label">Price</span>
          <input
            inputMode="decimal"
            value={limitPrice}
            onChange={(e) => setLimitPrice(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="0.00"
          />
          <button
            type="button"
            className="x-field-chip"
            onClick={() => refPrice && setLimitPrice(refPrice.toFixed(priceDecimals(refPrice)))}
            title="Use last price"
          >
            Last
          </button>
          <span className="x-field-unit">USD</span>
        </label>
      ) : (
        <div className="x-field x-field-ro">
          <span className="x-field-label">Price</span>
          <span className="x-field-ro-val">Market ≈ {fmtPrice(execPrice)}</span>
          <span className="x-field-unit">USD</span>
        </div>
      )}

      <label className="x-field">
        <span className="x-field-label">Size</span>
        <input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0"
        />
        <span className="x-field-unit">{base}</span>
      </label>

      <div className="x-of-slider">
        <Slider
          size="small"
          value={pct}
          marks={PCT_MARKS}
          step={1}
          min={0}
          max={100}
          disabled={!maxQty}
          onChange={(_, v) => setPct(v)}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${Math.round(v)}%`}
          className={`x-slider ${sideCls}`}
        />
        <div className="x-of-pcts">
          {[25, 50, 75, 100].map((p) => (
            <button key={p} type="button" disabled={!maxQty} onClick={() => setPct(p)}>
              {p}%
            </button>
          ))}
        </div>
      </div>

      <div className="x-of-summary">
        <div className="x-of-row">
          <span className="x-muted">Order value</span>
          <span>{qty ? fmtUsd(usdValue) : '—'}</span>
        </div>
        <div className="x-of-row">
          <span className="x-muted">{side === 'buy' ? 'Est. cost' : 'Est. receive'}</span>
          <span>{qty && quoteValue !== null ? `${fmtQty(quoteValue)} ${quote.symbol}` : '—'}</span>
        </div>
        <div className="x-of-row">
          <span className="x-muted">{quote.symbol} price</span>
          <span>{quote.price ? fmtUsd(quote.price) : '—'}</span>
        </div>
      </div>

      {connected ? (
        <button
          type="button"
          className={`x-of-submit ${sideCls}`}
          disabled={invalid || insufficient || submitting}
          onClick={() => setConfirmOpen(true)}
        >
          {insufficient
            ? `Insufficient ${side === 'buy' ? quote.symbol : base}`
            : `${kind === 'market' ? 'Market' : 'Limit'} ${side === 'buy' ? 'Buy' : 'Sell'} ${base}`}
        </button>
      ) : (
        <button type="button" className="x-of-submit x-connect" onClick={connectWallet}>
          Connect Wallet
        </button>
      )}

      {connected && walletAssets && (
        <div className="x-of-account">
          <div className="x-of-account-title">Account</div>
          <div className="x-of-row">
            <span className="x-muted">Portfolio value</span>
            <span>{fmtUsd(walletAssets.total)}</span>
          </div>
          <div className="x-of-row">
            <span className="x-muted">{quote.symbol} balance</span>
            <span>{fmtQty(quoteBal ?? 0)}</span>
          </div>
          <div className="x-of-row">
            <span className="x-muted">{base} balance</span>
            <span>{fmtQty(baseBal ?? 0)}</span>
          </div>
        </div>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        PaperProps={{ className: 'x-dialog' }}
      >
        <div className="x-dialog-head">
          <img src={tokenLogo(base)} alt="" onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />
          <div>
            <div className="x-dialog-title">
              Confirm {kind === 'market' ? 'market' : 'limit'}{' '}
              <span className={side === 'buy' ? 'x-up' : 'x-down'}>{side}</span>
            </div>
            <div className="x-muted">{base} / {quote.symbol}</div>
          </div>
        </div>
        <div className="x-dialog-body">
          <div className="x-of-row"><span className="x-muted">Side</span><span className={side === 'buy' ? 'x-up' : 'x-down'}>{side === 'buy' ? 'Buy' : 'Sell'}</span></div>
          <div className="x-of-row"><span className="x-muted">Type</span><span>{kind === 'market' ? 'Market' : 'Limit'}</span></div>
          <div className="x-of-row"><span className="x-muted">Price</span><span>{kind === 'market' ? `≈ ${fmtPrice(execPrice)}` : fmtPrice(execPrice)} USD</span></div>
          <div className="x-of-row"><span className="x-muted">Size</span><span>{fmtQty(qty)} {base}</span></div>
          <div className="x-of-row"><span className="x-muted">Order value</span><span>{fmtUsd(usdValue)}</span></div>
          {quoteValue !== null && (
            <div className="x-of-row"><span className="x-muted">{side === 'buy' ? 'Est. cost' : 'Est. receive'}</span><span>{fmtQty(quoteValue)} {quote.symbol}</span></div>
          )}
          {kind === 'market' && (
            <p className="x-note">Market orders fill at the best available price; the final amount can differ from this estimate.</p>
          )}
        </div>
        <div className="x-dialog-actions">
          <button type="button" className="x-btn-ghost" disabled={submitting} onClick={() => setConfirmOpen(false)}>
            Cancel
          </button>
          <button type="button" className={`x-of-submit ${sideCls}`} disabled={submitting} onClick={submit}>
            {submitting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : `Confirm ${side === 'buy' ? 'Buy' : 'Sell'}`}
          </button>
        </div>
      </Dialog>
    </div>
  );
}
